import { useState, useEffect, useCallback } from "react";

interface TOCSection {
  id: string;
  label: string;
  number: number;
}

interface TableOfContentsProps {
  sections: TOCSection[];
}

export default function TableOfContents({ sections }: TableOfContentsProps) {
  const [activeSection, setActiveSection] = useState(sections[0]?.id || "");

  const updateActiveSection = useCallback(() => {
    const headerOffset = 130;
    let current = sections[0]?.id || "";

    for (const section of sections) {
      const el = document.getElementById(section.id);
      if (el) {
        const rect = el.getBoundingClientRect();
        if (rect.top <= headerOffset) {
          current = section.id;
        }
      }
    }

    // Also check if we're at the bottom of the page - if so, activate last section
    const scrollBottom = window.innerHeight + window.scrollY;
    const docHeight = document.documentElement.scrollHeight;
    if (scrollBottom >= docHeight - 50) {
      current = sections[sections.length - 1]?.id || current;
    }

    setActiveSection(current);
  }, [sections]);

  useEffect(() => {
    window.addEventListener("scroll", updateActiveSection, { passive: true });
    updateActiveSection();
    return () => window.removeEventListener("scroll", updateActiveSection);
  }, [updateActiveSection]);

  const handleClick = (e: React.MouseEvent, id: string) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      const headerHeight = 100;
      const top = el.getBoundingClientRect().top + window.scrollY - headerHeight;
      window.scrollTo({ top, behavior: "smooth" });
    }
    window.history.pushState(null, "", `#${id}`);
  };

  return (
    <nav
      className="sticky top-28"
      aria-label="Table of contents"
    >
      <h4 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-4">
        On this page
      </h4>
      <ul className="space-y-0.5 border-l border-background-200">
        {sections.map((section) => {
          const isActive = activeSection === section.id;
          return (
            <li key={section.id}>
              <a
                href={`#${section.id}`}
                onClick={(e) => handleClick(e, section.id)}
                className={`block py-2.5 pl-4 pr-3 text-sm rounded-r-md transition-all duration-200 border-l-2 -ml-[1px] whitespace-nowrap ${
                  isActive
                    ? "border-primary-500 text-primary-700 font-semibold bg-primary-50/70"
                    : "border-transparent text-foreground-600 hover:text-foreground-900 hover:border-foreground-300 hover:bg-background-100"
                }`}
              >
                <span className="inline-flex items-center gap-2.5">
                  <span
                    className={`w-5 h-5 flex items-center justify-center text-[11px] font-bold rounded-full transition-colors duration-200 ${
                      isActive
                        ? "bg-primary-500 text-background-50"
                        : "bg-background-200 text-foreground-500"
                    }`}
                  >
                    {section.number}
                  </span>
                  {section.label}
                </span>
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}